// ═══════════════════════════════════════════════════════════════════════════
//  MANAGED SYMBOLS STORE — the Global Symbol Manager as the ONE symbol truth.
//
//  TWO-TIER ARCHITECTURE (user directive 2026-07-10, "Active Portfolio"):
//  • DICTIONARY (enabled rows): metadata SSOT — selectable in EVERY dropdown
//    (bots, chart search). Zero data cost: a row's existence never triggers
//    downloads or deep-math.
//  • ACTIVE PORTFOLIO (screener === true): the VIP subset the heavy engine
//    runs for — screener tabs/polling, SMC matrix deep-math, historical data.
//    Server /smc-matrix enforces the same flag (AWS circuit breaker).
//
//  One shared cache + a change broadcast: when the SYMBOL MANAGER saves a row
//  it calls notifyManagedSymbolsChanged() and every subscribed consumer
//  re-fetches and re-renders — no hard refresh, no server restart. A
//  localStorage revision key carries the same signal across browser tabs.
//
//  Honest-data rule: if the API is unreachable the store returns its last
//  good rows (or []) and consumers keep their static fallback lists — it
//  never invents symbols.
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useState } from 'react';
import { apiGet } from '../api';

export type ManagedCategory = 'INDEX' | 'CASH' | 'FUTURES' | 'COMMODITY' | 'ETF';

export interface ManagedSymbolRow {
  symbol: string;            // canonical UPPER_SNAKE (DB key)
  name: string;
  category: ManagedCategory;
  exch: string;              // IDX_I / NSE_EQ / NSE_FNO / MCX_COMM …
  secId: string;
  lotSize: number;
  marginAmount: number;
  strikeStep: number;
  expiry: string;
  enabled: boolean;
  screener?: boolean;
  updatedAt?: string;
}

const EVT = 'jc:managed-symbols-changed';
const LS_REV = 'jc_managed_symbols_rev';
const TTL_MS = 5 * 60_000;

let _cache: { rows: ManagedSymbolRow[]; at: number } | null = null;
let _inflight: Promise<ManagedSymbolRow[]> | null = null;

export async function fetchManagedSymbols(force = false): Promise<ManagedSymbolRow[]> {
  if (!force && _cache && Date.now() - _cache.at < TTL_MS) return _cache.rows;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    const r = await apiGet<{ status: string; symbols: ManagedSymbolRow[] }>(
      '/api/symbol-manager/list');
    const rows = r.ok && r.data?.status === 'ok' && Array.isArray(r.data.symbols)
      ? r.data.symbols.filter(s => s.enabled !== false)
      : (_cache?.rows ?? []);
    _cache = { rows, at: Date.now() };
    _inflight = null;
    return rows;
  })();
  return _inflight;
}

/** Call after any Symbol Manager save/delete — every consumer re-fetches. */
export function notifyManagedSymbolsChanged(): void {
  _cache = null;
  try { window.dispatchEvent(new CustomEvent(EVT)); } catch { /* SSR/tests */ }
  try { localStorage.setItem(LS_REV, String(Date.now())); } catch { /* private mode */ }
}

export function subscribeManagedSymbols(cb: () => void): () => void {
  const onEvt = () => cb();
  const onStorage = (e: StorageEvent) => { if (e.key === LS_REV) { _cache = null; cb(); } };
  window.addEventListener(EVT, onEvt);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVT, onEvt);
    window.removeEventListener('storage', onStorage);
  };
}

/** Screener-visible managed rows, live-updating on Symbol Manager saves. */
export function useManagedSymbols(category?: ManagedCategory): ManagedSymbolRow[] {
  const [rows, setRows] = useState<ManagedSymbolRow[]>(() => _cache?.rows ?? []);
  useEffect(() => {
    let alive = true;
    fetchManagedSymbols().then(r => { if (alive) setRows(r); });
    const un = subscribeManagedSymbols(() => {
      fetchManagedSymbols(true).then(r => { if (alive) setRows(r); });
    });
    return () => { alive = false; un(); };
  }, []);
  return useMemo(
    () => (category ? rows.filter(r => r.category === category) : rows),
    [rows, category]);
}

// ─── Canonical DB symbol ↔ system apiSym / display name ──────────────────────
// The DB keeps Dhan-style canonicals (NIFTY, BANKNIFTY); the quote engine and
// the existing UI use the screener forms (NIFTY_50, BANK_NIFTY, "NIFTY 50").
const API_SYM_ALIAS: Record<string, string> = {
  NIFTY: 'NIFTY_50', BANKNIFTY: 'BANK_NIFTY', FINNIFTY: 'FIN_NIFTY',
};

export const managedApiSym = (row: ManagedSymbolRow): string =>
  API_SYM_ALIAS[row.symbol] || row.symbol;

/** Active Portfolio membership — heavy data (screener/SMC/5Y) sirf in rows par. */
export const isPortfolioRow = (row: ManagedSymbolRow): boolean => row.screener === true;

/** Display name whose `spaces→underscores` normalisation equals the apiSym —
 *  required by SuperChart's toApiSym() fallback for symbols outside its
 *  static registry. */
export const managedDisplayName = (row: ManagedSymbolRow): string =>
  managedApiSym(row).replace(/_/g, ' ');
